import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { ArrowDownToLine, Plus, Trash2, Pause, Play, RotateCw, Check, X, Copy, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';

const ACCENT = '#f97316';

interface InboundConfig {
  id: string;
  name: string;
  description: string;
  slug: string;
  status: 'active' | 'paused' | 'disabled';
  verification: {
    type: 'none' | 'hmac_sha256' | 'basic_auth' | 'header_token';
    secret?: string;
    header_name?: string;
    username?: string;
  };
  transform: {
    payload_path?: string;
    field_mappings: { source_path: string; target_field: string }[];
  };
  routing: {
    target_type: 'bus_event' | 'product_action' | 'flow_trigger';
    bus_event_type?: string;
    product?: string;
    action?: string;
    flow_id?: string;
  };
  request_count: number;
  last_received_at: string | null;
  created_at: string;
}

interface LogEntry {
  id: string;
  config_id: string;
  status: 'received' | 'processed' | 'failed' | 'rejected';
  method: string;
  ip_address: string;
  error?: string;
  received_at: string;
  processed_at: string | null;
}

const WEBHOOK_BASE = 'https://api.theonebridge.app/hooks/in';

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function InboundWebhooksPage() {
  const [configs, setConfigs] = useState<InboundConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<string | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState<string | null>(null);
  const [secretValue, setSecretValue] = useState<string | null>(null);

  // Create form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [verificationType, setVerificationType] = useState<'none' | 'hmac_sha256' | 'basic_auth' | 'header_token'>('none');
  const [headerName, setHeaderName] = useState('X-Webhook-Token');
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [routingType, setRoutingType] = useState<'bus_event'>('bus_event');
  const [busEventType, setBusEventType] = useState('');
  const [payloadPath, setPayloadPath] = useState('');
  const [creating, setCreating] = useState(false);

  const loadConfigs = useCallback(async () => {
    try {
      const data = await api.get<{ configs: InboundConfig[] }>('/inbound-webhooks');
      setConfigs(data.configs);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      await api.post('/inbound-webhooks', {
        name: name.trim(),
        description: description.trim(),
        verification: {
          type: verificationType,
          ...(verificationType === 'header_token' && { header_name: headerName }),
          ...(verificationType === 'basic_auth' && { username: basicUsername, password: basicPassword }),
        },
        transform: {
          payload_path: payloadPath || undefined,
          field_mappings: [],
        },
        routing: {
          target_type: routingType,
          ...(routingType === 'bus_event' && { bus_event_type: busEventType || undefined }),
        },
      });
      setShowCreate(false);
      resetForm();
      loadConfigs();
    } catch (err: any) {
      alert(err.message || 'Failed to create');
    }
    setCreating(false);
  };

  const resetForm = () => {
    setName(''); setDescription(''); setVerificationType('none');
    setHeaderName('X-Webhook-Token'); setBasicUsername(''); setBasicPassword('');
    setRoutingType('bus_event'); setBusEventType(''); setPayloadPath('');
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/inbound-webhooks/${id}`);
      setConfirmDelete(null);
      loadConfigs();
    } catch { /* empty */ }
  };

  const handleToggle = async (cfg: InboundConfig) => {
    const newStatus = cfg.status === 'active' ? 'paused' : 'active';
    try {
      await api.patch(`/inbound-webhooks/${cfg.id}`, { status: newStatus });
      loadConfigs();
    } catch { /* empty */ }
  };

  const handleRegenerateSecret = async (id: string) => {
    try {
      const res = await api.post<{ secret: string }>(`/inbound-webhooks/${id}/regenerate-secret`);
      alert(`New secret: ${res.secret}\n\nUpdate your sending system immediately.`);
      loadConfigs();
    } catch (err: any) {
      alert(err.message || 'Failed to regenerate');
    }
  };

  const loadLogs = async (configId: string) => {
    if (expandedLogs === configId) {
      setExpandedLogs(null);
      return;
    }
    setExpandedLogs(configId);
    setLogsLoading(true);
    try {
      const data = await api.get<{ logs: LogEntry[] }>(`/inbound-webhooks/${configId}/logs`);
      setLogs(data.logs);
    } catch { setLogs([]); }
    setLogsLoading(false);
  };

  const copyUrl = (cfg: InboundConfig) => {
    // We use tenant slug from the URL — get it from cookies or config
    // For display, we show {tenant_slug} placeholder
    const url = `${WEBHOOK_BASE}/{tenant_slug}/${cfg.slug}`;
    navigator.clipboard.writeText(url);
    setCopiedId(cfg.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const viewSecret = async (id: string) => {
    if (showSecret === id) {
      setShowSecret(null);
      setSecretValue(null);
      return;
    }
    try {
      const data = await api.get<{ config: InboundConfig & { verification: { secret?: string } } }>(`/inbound-webhooks/${id}`);
      setSecretValue(data.config.verification.secret || '');
      setShowSecret(id);
    } catch { /* empty */ }
  };

  const statusColor = (s: string) =>
    s === 'active' ? { bg: '#dcfce7', color: '#166534' } :
    s === 'paused' ? { bg: '#fef9c3', color: '#854d0e' } :
    { bg: '#fee2e2', color: '#991b1b' };

  const logStatusColor = (s: string) =>
    s === 'processed' ? { bg: '#dcfce7', color: '#166534' } :
    s === 'received' ? { bg: '#dbeafe', color: '#1e40af' } :
    s === 'rejected' ? { bg: '#fee2e2', color: '#991b1b' } :
    { bg: '#fef3c7', color: '#92400e' };

  if (loading) return <p style={{ color: '#6b7280' }}>Loading inbound webhooks...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Inbound Webhooks</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
            Receive webhooks from external systems, transform payloads, and route to product actions
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} style={btnStyle}>
          <Plus size={16} /> New Inbound Hook
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem' }}>Create Inbound Webhook</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              <div>
                <label style={labelStyle}>Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="GitHub Push Events" style={inputStyle} />
                {name && (
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>
                    Slug: <code>{toSlug(name)}</code>
                  </p>
                )}
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Receives push events from GitHub" style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Verification</label>
                <select
                  value={verificationType}
                  onChange={e => setVerificationType(e.target.value as typeof verificationType)}
                  style={inputStyle}
                >
                  <option value="none">None (open)</option>
                  <option value="hmac_sha256">HMAC-SHA256 Signature</option>
                  <option value="basic_auth">Basic Auth</option>
                  <option value="header_token">Header Token</option>
                </select>
                {verificationType === 'basic_auth' && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                    <input value={basicUsername} onChange={e => setBasicUsername(e.target.value)} placeholder="Username" style={{ ...inputStyle, flex: 1 }} />
                    <input value={basicPassword} onChange={e => setBasicPassword(e.target.value)} placeholder="Password" type="password" style={{ ...inputStyle, flex: 1 }} />
                  </div>
                )}
                {verificationType === 'header_token' && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <input value={headerName} onChange={e => setHeaderName(e.target.value)} placeholder="Header name" style={inputStyle} />
                  </div>
                )}
                {verificationType === 'hmac_sha256' && (
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                    A secret will be generated automatically. Send HMAC-SHA256 signature in X-Bridge-Signature header.
                  </p>
                )}
              </div>

              <div>
                <label style={labelStyle}>Routing</label>
                <select
                  value={routingType}
                  onChange={e => setRoutingType(e.target.value as typeof routingType)}
                  style={inputStyle}
                >
                  <option value="bus_event">Bus Event</option>
                </select>
                {routingType === 'bus_event' && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <input
                      value={busEventType}
                      onChange={e => setBusEventType(e.target.value)}
                      placeholder="bridge.inbound_webhook.received (default)"
                      style={inputStyle}
                    />
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Payload Path (optional)</label>
                <input
                  value={payloadPath}
                  onChange={e => setPayloadPath(e.target.value)}
                  placeholder="e.g. data.payload"
                  style={inputStyle}
                />
                <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>
                  Extract a nested object from the incoming payload before routing
                </p>
              </div>
            </div>

            <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowCreate(false); resetForm(); }} style={{ ...btnStyle, backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>Cancel</button>
              <button
                onClick={handleCreate}
                disabled={creating || !name.trim()}
                style={{ ...btnStyle, opacity: creating ? 0.6 : 1 }}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {configs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <ArrowDownToLine size={32} color="#9ca3af" style={{ margin: '0 auto 0.75rem' }} />
          <p style={{ color: '#6b7280', margin: '0 0 1rem' }}>No inbound webhooks configured</p>
          <button onClick={() => setShowCreate(true)} style={btnStyle}>Create Inbound Hook</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {configs.map(cfg => {
            const sc = statusColor(cfg.status);
            return (
              <div key={cfg.id} style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                <div style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <ArrowDownToLine size={16} color="#9ca3af" style={{ marginTop: '0.125rem', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{cfg.name}</span>
                        <span style={{ padding: '0.125rem 0.5rem', fontSize: '0.6875rem', borderRadius: '9999px', backgroundColor: sc.bg, color: sc.color, fontWeight: 500 }}>
                          {cfg.status}
                        </span>
                        <span style={{ padding: '0.125rem 0.5rem', fontSize: '0.6875rem', borderRadius: '9999px', backgroundColor: '#ede9fe', color: '#5b21b6', fontWeight: 500 }}>
                          {cfg.verification.type === 'none' ? 'open' : cfg.verification.type}
                        </span>
                        <span style={{ padding: '0.125rem 0.5rem', fontSize: '0.6875rem', borderRadius: '9999px', backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 500 }}>
                          {cfg.routing.target_type}
                        </span>
                      </div>

                      {cfg.description && (
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.25rem' }}>{cfg.description}</p>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <code style={{ fontSize: '0.75rem', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>
                          {WEBHOOK_BASE}/{'{'}<span style={{ color: ACCENT }}>tenant_slug</span>{'}'}/{cfg.slug}
                        </code>
                        <button
                          onClick={() => copyUrl(cfg)}
                          title="Copy URL"
                          style={{ ...iconBtnSmall, color: copiedId === cfg.id ? '#16a34a' : '#6b7280' }}
                        >
                          {copiedId === cfg.id ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                      </div>

                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                        <span>{cfg.request_count.toLocaleString()} requests</span>
                        {cfg.last_received_at && (
                          <span>Last: {new Date(cfg.last_received_at).toLocaleString()}</span>
                        )}
                      </div>

                      {/* Secret display */}
                      {(cfg.verification.type === 'hmac_sha256' || cfg.verification.type === 'header_token') && showSecret === cfg.id && secretValue && (
                        <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#fef3c7', borderRadius: '0.25rem', fontSize: '0.75rem' }}>
                          <span style={{ fontWeight: 500 }}>Secret: </span>
                          <code style={{ wordBreak: 'break-all' }}>{secretValue}</code>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                      {/* View secret */}
                      {(cfg.verification.type === 'hmac_sha256' || cfg.verification.type === 'header_token') && (
                        <button onClick={() => viewSecret(cfg.id)} title={showSecret === cfg.id ? 'Hide secret' : 'Show secret'} style={iconBtnStyle}>
                          {showSecret === cfg.id ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                      {/* Regenerate secret */}
                      {(cfg.verification.type === 'hmac_sha256' || cfg.verification.type === 'header_token') && (
                        <button onClick={() => handleRegenerateSecret(cfg.id)} title="Regenerate secret" style={iconBtnStyle}>
                          <RotateCw size={14} />
                        </button>
                      )}
                      {/* Pause/Resume */}
                      {cfg.status !== 'disabled' && (
                        <button onClick={() => handleToggle(cfg)} title={cfg.status === 'active' ? 'Pause' : 'Resume'} style={iconBtnStyle}>
                          {cfg.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                      )}
                      {/* Logs */}
                      <button onClick={() => loadLogs(cfg.id)} title="View logs" style={iconBtnStyle}>
                        {expandedLogs === cfg.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {/* Delete */}
                      {confirmDelete === cfg.id ? (
                        <>
                          <button onClick={() => handleDelete(cfg.id)} style={{ ...iconBtnStyle, color: '#dc2626' }} title="Confirm"><Check size={14} /></button>
                          <button onClick={() => setConfirmDelete(null)} style={iconBtnStyle} title="Cancel"><X size={14} /></button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmDelete(cfg.id)} style={iconBtnStyle} title="Delete"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Logs panel */}
                {expandedLogs === cfg.id && (
                  <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.75rem 1rem', backgroundColor: '#f9fafb' }}>
                    <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Recent Requests</p>
                    {logsLoading ? (
                      <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Loading...</p>
                    ) : logs.length === 0 ? (
                      <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>No requests received yet</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {logs.slice(0, 50).map(log => {
                          const lsc = logStatusColor(log.status);
                          return (
                            <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', padding: '0.375rem 0' }}>
                              <span style={{ padding: '0.0625rem 0.375rem', borderRadius: '0.25rem', backgroundColor: lsc.bg, color: lsc.color, fontWeight: 500, minWidth: 60, textAlign: 'center' }}>
                                {log.status}
                              </span>
                              <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>{log.method}</span>
                              <span style={{ color: '#9ca3af' }}>{log.ip_address}</span>
                              {log.error && <span style={{ color: '#dc2626' }}>{log.error}</span>}
                              <span style={{ marginLeft: 'auto', color: '#9ca3af' }}>
                                {new Date(log.received_at).toLocaleString()}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
  padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
  backgroundColor: ACCENT, color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
};
const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '2rem', height: '2rem', backgroundColor: 'transparent',
  border: '1px solid #e5e7eb', borderRadius: '0.375rem', cursor: 'pointer', color: '#6b7280',
};
const iconBtnSmall: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '1.5rem', height: '1.5rem', backgroundColor: 'transparent',
  border: 'none', borderRadius: '0.25rem', cursor: 'pointer', padding: 0,
};
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
};
const modalStyle: React.CSSProperties = {
  backgroundColor: '#fff', borderRadius: '0.75rem', padding: '1.5rem',
  width: '100%', maxWidth: '480px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.5rem', fontSize: '0.875rem',
  border: '1px solid #d1d5db', borderRadius: '0.375rem', boxSizing: 'border-box',
};
